import { FunctionComponent, Fragment } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { vatMessages } from 'dibs-vat-utils/exports/vatMessages';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { changeQuantity } from '../../actions/checkoutUIActions';
import { trackFormAction } from '../../helpers/checkoutTracking';
import { CheckoutMobileSummaryPriceTableRow } from './CheckoutMobileSummaryPriceTableRow';
import { CheckoutMobileSummaryQuantitySelect } from './CheckoutMobileSummaryQuantitySelect';
import { CheckoutMobileSummaryPriceTable_transaction$key } from './__generated__/CheckoutMobileSummaryPriceTable_transaction.graphql';
import { CheckoutMobileSummaryPriceTable_item$key } from './__generated__/CheckoutMobileSummaryPriceTable_item.graphql';
import styles from './styles/CheckoutMobileSummaryPriceTable.scss';

type CheckoutMobileSummaryPriceTableProps = {
    transaction: CheckoutMobileSummaryPriceTable_transaction$key;
    item: CheckoutMobileSummaryPriceTable_item$key;
    isComputing: boolean;
};

export const CheckoutMobileSummaryPriceTable: FunctionComponent<
    CheckoutMobileSummaryPriceTableProps
> = props => {
    const { isComputing } = props;

    const dispatch = useDispatch();
    const currentQuantity = useSelector<$TSFixMe, number>(state => state?.transaction?.quantity);
    const paymentMethodToken = useSelector<$TSFixMe, string>(
        state => state?.transaction?.paymentMethodToken
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutMobileSummaryPriceTable_transaction on Transaction {
                ...CheckoutMobileSummaryQuantitySelect_transaction
                buyerPriceTables(displayCurrency: $displayCurrency) {
                    tables {
                        type
                        rows {
                            label
                            value
                            rowType
                            formatStyle
                            hasTopBorder
                            subLabel
                        }
                    }
                }
                vatAmount {
                    amount
                }
                priceBookName
            }
        `,
        props.transaction
    );

    const item = useFragment(
        graphql`
            fragment CheckoutMobileSummaryPriceTable_item on Item {
                canMultipleQuantityBePurchased
            }
        `,
        props.item
    );

    function handleQuantityChange(quantity: number): void {
        trackFormAction('quantity changed');
        changeQuantity(dispatch, {
            quantity,
            paymentMethodToken,
        });
    }
    const priceBookName = transaction?.priceBookName;
    const priceRows =
        transaction?.buyerPriceTables?.tables?.find(table => table?.type === 'MAIN')?.rows || [];
    const listPrice = priceRows.find(row => row?.rowType === 'LIST_PRICE');
    const hasNonListPrices = priceRows.some(row => row?.rowType === 'PRICE');
    const hasMultipleQuantity = item?.canMultipleQuantityBePurchased;

    const wrapperClasses = classnames(styles.wrapper, {
        [styles.borderTop]: !hasNonListPrices,
    });

    return (
        <div className={wrapperClasses}>
            {priceRows.map((row, index) => {
                switch (row?.rowType) {
                    case 'LIST_PRICE':
                        return hasNonListPrices ? null : (
                            <CheckoutMobileSummaryPriceTableRow
                                {...row}
                                key={`${row?.rowType}-${index}`}
                            />
                        );
                    case 'QUANTITY':
                        return hasMultipleQuantity ? (
                            <CheckoutMobileSummaryPriceTableRow
                                {...row}
                                value={
                                    <CheckoutMobileSummaryQuantitySelect
                                        transaction={transaction}
                                        quantity={currentQuantity}
                                        onChange={handleQuantityChange}
                                    />
                                }
                                key={`${row?.rowType}-${index}`}
                            />
                        ) : (
                            <CheckoutMobileSummaryPriceTableRow
                                {...row}
                                key={`${row?.rowType}-${index}`}
                            />
                        );
                    case 'PRICE': {
                        return (
                            <CheckoutMobileSummaryPriceTableRow
                                {...row}
                                discountFrom={listPrice?.value}
                                key={`${row?.rowType}-${index}`}
                            />
                        );
                    }
                    case 'PRICE_DISCOUNT':
                    case 'NEGOTIATED_DISCOUNT':
                        return null;
                    case 'TOTAL': {
                        return (
                            <Fragment key={`${row?.rowType}-${index}`}>
                                <CheckoutMobileSummaryPriceTableRow
                                    {...row}
                                    isMainPrice
                                    value={
                                        <>
                                            {isComputing && (
                                                <span className={styles.spinner}>
                                                    <Spinner size="tiny" />
                                                </span>
                                            )}{' '}
                                            {row?.value}
                                        </>
                                    }
                                />
                                {!!transaction?.vatAmount && (
                                    <div className={styles.vatLabel}>
                                        {vatMessages.included({ priceBookName })}
                                    </div>
                                )}
                            </Fragment>
                        );
                    }
                    default:
                        return (
                            <CheckoutMobileSummaryPriceTableRow
                                {...row}
                                key={`${row?.rowType}-${index}`}
                            />
                        );
                }
            })}
        </div>
    );
};
